import { Bell, CalendarPlus, Circle, CircleCheckBig, CircleDot, Copy, X } from "lucide-react";
import DetailedListItem from "@/components/tasks/lists/DetailedListItem/DetailedListItem";
import SimpleTaskListItem from "@/components/tasks/lists/SimpleTaskListItem/SimpleTaskListItem";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDurationShort, formatTimeDisplay } from "@/lib/time";
import {
  deriveTaskLifecycle,
  getTaskLifecycleLabel,
  type TaskLifecycle,
} from "@shared/taskLifecycle";

export type ListTaskRowTask = {
  id: number;
  title: string;
  details?: string | null;
  date?: string | null;
  status: "pending" | "done" | "skipped";
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled: boolean;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  notes?: string | null;
  log?: string | null;
  barColor?: string | null;
  bgColor?: string | null;
  listColor?: string | null;
  listBgColor?: string | null;
};

function dateToHHMM(value: Date | string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
}

function resultDurationMinutes(
  startedAt: Date | string | null | undefined,
  completedAt: Date | string | null | undefined,
) {
  if (!startedAt || !completedAt) return 0;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.round((end - start) / 60000);
}

function AreaTaskStatusIcon({
  status,
}: {
  status: TaskLifecycle;
}) {
  if (status === "doing") {
    return <CircleDot className="h-4 w-4" aria-label={getTaskLifecycleLabel(status)} />;
  }
  if (status === "done") {
    return <CircleCheckBig className="h-4 w-4" aria-label="Done" />;
  }
  if (status === "skipped") {
    return <X className="h-4 w-4" aria-label="Skipped" />;
  }
  return <Circle className="h-4 w-4" aria-label="Pending" />;
}

export default function ListTaskRow({
  task,
  selected = false,
  selectMode = false,
  variant = "detailed",
  showSchedule = true,
  onOpen,
  onAddToToday,
  onDuplicate,
}: {
  task: ListTaskRowTask;
  selected?: boolean;
  selectMode?: boolean;
  variant?: "simple" | "detailed";
  showSchedule?: boolean;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onAddToToday: () => void;
  onDuplicate: () => void;
}) {
  const colors = resolveTaskColors({
    barColor: task.barColor,
    bgColor: task.bgColor,
    listColor: task.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: task.listBgColor ?? null,
  });
  const lifecycle = deriveTaskLifecycle(task);
  const isDimmed = lifecycle === "done" || lifecycle === "skipped";
  const resultDuration = resultDurationMinutes(task.startedAt, task.completedAt);
  const started = dateToHHMM(task.startedAt);
  const completed = dateToHHMM(task.completedAt);
  const schedule = showSchedule
    ? [
        task.date ? task.date.slice(5) : "no day",
        task.startMinutes != null ? formatTimeDisplay(task.startMinutes) : null,
        task.durationMin ? formatDurationShort(task.durationMin) : null,
      ].filter(Boolean)
    : [];
  const statusTime =
    lifecycle === "doing" || lifecycle === "done"
      ? started || completed
      : lifecycle === "skipped"
        ? completed || started
        : started;
  const statusName = lifecycle === "pending" ? null : getTaskLifecycleLabel(lifecycle);
  const statusDetails = [
    statusName,
    statusTime || null,
    resultDuration > 0 ? formatDurationShort(resultDuration) : null,
  ].filter(Boolean);
  const statusLabel = getTaskLifecycleLabel(lifecycle);
  const icon = (
    <>
      <AreaTaskStatusIcon status={lifecycle} />
      <span className="sr-only">{statusLabel}</span>
    </>
  );
  const title = (
    <span className={task.status === "skipped" ? "line-through" : ""}>
      {task.title}
    </span>
  );
  const meta = (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="truncate">{schedule.join(" | ")}</span>
      {task.alertEnabled && (
        <Bell className="h-3 w-3 shrink-0" aria-label="Alert on" />
      )}
    </span>
  );
  const actions = !selectMode ? (
    <div className="flex h-5 items-center gap-1">
      <button
        type="button"
        data-sheet-ignore
        onClick={onAddToToday}
        className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
        aria-label={`Add ${task.title} to today`}
        title="Add to today"
      >
        <CalendarPlus className="h-4 w-4" />
      </button>
      <button
        type="button"
        data-sheet-ignore
        onClick={onDuplicate}
        className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
        aria-label={`Duplicate ${task.title}`}
        title="Duplicate"
      >
        <Copy className="h-4 w-4" />
      </button>
    </div>
  ) : null;

  if (variant === "simple") {
    const simpleMeta = [
      ...schedule,
      ...statusDetails,
    ].filter(Boolean).join(" | ");

    return (
      <SimpleTaskListItem
        icon={<span style={{ color: colors.bar }}>{icon}</span>}
        title={title}
        meta={simpleMeta || null}
        details={task.details}
        right={actions}
        selected={selected}
        dimmed={isDimmed}
        color={colors.ink}
        onClick={onOpen}
      />
    );
  }

  return (
    <DetailedListItem
      icon={icon}
      title={title}
      meta={meta}
      details={task.details}
      notes={task.notes}
      statusDetails={statusDetails.length > 0 ? statusDetails.join(" ") : null}
      right={actions}
      selected={selected}
      dimmed={isDimmed}
      colors={colors}
      onClick={onOpen}
    />
  );
}
